import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/components/auth-provider";

/**
 * RouteGuard Component
 * Protects routes based on authentication status
 * Shows loading state while checking auth
 * Redirects based on authentication requirements
 */

type RouteGuardProps = {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
  fallbackTo?: string;
};

export function RouteGuard({
  children,
  requireAuth = false,
  redirectTo = "/auth/signin",
  fallbackTo = "/dashboard",
}: RouteGuardProps) {
  const { user, status } = useAuth();
  const navigate = useNavigate();
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    // Don't redirect while still loading
    if (status === "loading") return;

    const isAuthenticated = user !== null;
    const shouldRedirect = requireAuth ? !isAuthenticated : isAuthenticated;

    if (shouldRedirect) {
      const targetPath = requireAuth ? redirectTo : fallbackTo;

      // Prevent infinite redirect loops
      const currentPath = window.location.pathname;
      if (currentPath !== targetPath) {
        setIsRedirecting(true);
        navigate(targetPath, { replace: true });
      }
    } else {
      setIsRedirecting(false);
    }
  }, [user, status, requireAuth, redirectTo, fallbackTo, navigate]);

  // Show loading spinner while checking authentication
  if (status === "loading" || isRedirecting) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">
            {isRedirecting ? "Redirecting..." : "Checking authentication..."}
          </p>
        </div>
      </div>
    );
  }

  // Render children only when auth status is confirmed
  if (requireAuth && !user) {
    return null; // Will be redirected
  }

  if (!requireAuth && user) {
    return null; // Will be redirected
  }

  return <>{children}</>;
}

/**
 * ProtectedRoute Component
 * For routes that require authentication (dashboard, settings, etc.)
 */
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  return <RouteGuard requireAuth={true}>{children}</RouteGuard>;
}

/**
 * PublicRoute Component
 * For routes that should not be accessed when authenticated (signin, signup)
 */
export function PublicRoute({ children }: { children: React.ReactNode }) {
  return (
    <RouteGuard requireAuth={false} fallbackTo="/dashboard">
      {children}
    </RouteGuard>
  );
}

/**
 * LandlordRoute Component
 * For landlord-only routes with role-based protection
 * Admin and super-admin can access all pages
 */
export function LandlordRoute({ children }: { children: React.ReactNode }) {
  return (
    <RouteGuard requireAuth={true}>
      <RoleGuard allowedRoles={["landlord", "admin", "super-admin"]}>
        {children}
      </RoleGuard>
    </RouteGuard>
  );
}

/**
 * RoleGuard Component
 * Protects routes based on user roles from Firebase Custom Claims (SECURE)
 * Admin and super-admin can bypass role restrictions
 *
 * Note: user.role comes from Firebase Custom Claims which are:
 * - Set server-side only (via Firebase Admin SDK)
 * - Cannot be modified by clients
 * - Included in the ID token
 */
type RoleGuardProps = {
  children: React.ReactNode;
  allowedRoles: ("landlord" | "tenant" | "admin" | "super-admin")[];
  redirectTo?: string;
};

export function RoleGuard({
  children,
  allowedRoles,
  redirectTo = "/dashboard",
}: RoleGuardProps) {
  const { user, status } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (status === "authenticated" && user) {
      // user.role comes from custom claims (secure, server-side only)
      const userRole = user.role || "tenant";

      // Admin and super-admin can access all pages
      if (userRole === "admin" || userRole === "super-admin") {
        return;
      }

      if (!allowedRoles.includes(userRole)) {
        navigate(redirectTo, { replace: true });
      }
    }
  }, [user, status, allowedRoles, redirectTo, navigate]);

  // Show loading or role check
  if (!user) {
    return null;
  }

  const userRole = user.role || "tenant";

  // Admin and super-admin can access all pages
  if (userRole === "admin" || userRole === "super-admin") {
    return <>{children}</>;
  }

  // Check if user has the required role
  if (!allowedRoles.includes(userRole)) {
    // The useEffect will handle the redirect
    return null;
  }

  return <>{children}</>;
}

// Convenience hooks for route protection

/**
 * Custom hook to get auth redirect helper
 */
export function useAuthRedirect() {
  const { user, status } = useAuth();
  const navigate = useNavigate();

  const redirectAuthenticated = (to?: string) => {
    if (status === "authenticated" && user) {
      navigate(to || "/dashboard", { replace: true });
      return true;
    }
    return false;
  };

  const redirectUnauthenticated = (to?: string) => {
    if (status !== "loading" && !user) {
      navigate(to || "/auth/signin", { replace: true });
      return true;
    }
    return false;
  };

  return {
    redirectAuthenticated,
    redirectUnauthenticated,
    isLoading: status === "loading",
    isAuthenticated: !!user,
  };
}
